'use client'

import { useState, useRef, useEffect } from 'react'
import { useAuth, SignUp, SignIn } from '@clerk/nextjs'
import api from '@/service/api'
import { apiEvents } from '@/service/request'
import { createReader, readStream } from '@/lib/stream'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Clock,
  FileText,
  Loader2,
  AlertCircle,
  UserPlus,
  X,
  Zap
} from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { getIconByName } from '@/lib/icon-map'
import { MarkdownRenderer } from '@/components/markdown-renderer'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { PaywallModal } from '@/components/PaywallModal'
import { MobileSignUpCTA } from '@/components/MobileSignUpCTA'
import { ToolAnalytics } from '@/lib/analytics'
import ClipPreviewPanel, { type ClipPreview } from '@/components/ClipPreviewPanel'

interface ToolPageClientProps {
  tool: any
  relatedTools: any[]
  categoryTools: any[]
}

interface TranscriptSnippet {
  text: string
  start: number
  duration: number
}

interface TranscriptData {
  snippets: TranscriptSnippet[]
}

interface TranscriptResponse {
  youtube_video_id: string
  youtube_url: string
  title?: string
  channel_name?: string
  thumbnail_url?: string
  duration?: number
  transcript: TranscriptData
  language: string
  is_auto_generated: boolean
  available_languages: string[]
}

export default function ToolPageClient({ tool, relatedTools, categoryTools }: ToolPageClientProps) {
  const { getToken, isSignedIn } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  // URL & Transcript State — initial URL from ?url= query param is read on the
  // client in the useEffect below. Reading searchParams in the parent server
  // component would opt the whole route out of SSG/CDN caching.
  const [url, setUrl] = useState("")
  const [transcript, setTranscript] = useState<TranscriptResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // AI Generation State
  const [generatedContent, setGeneratedContent] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)

  // UI State
  const [showTimestamps, setShowTimestamps] = useState(true)
  const [copied, setCopied] = useState(false)
  const [copiedGenerated, setCopiedGenerated] = useState(false)
  const [showWorkspace, setShowWorkspace] = useState(false)

  // Auth Modal State — starts false to avoid hydration mismatch.
  // useEffect checks for #/sso-callback hash after mount.
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authMode, setAuthMode] = useState<"sign-up" | "sign-in">("sign-up")
  // Tracks where auth modal should redirect: "tool" (default) stays on tool page, "configure" goes to /configure
  const [authRedirectTarget, setAuthRedirectTarget] = useState<"tool" | "configure">("tool")

  // Loading overlay: shown during OAuth SSO callback → /configure redirect transition
  const [redirectingToConfigure, setRedirectingToConfigure] = useState(false)

  // Open auth modal after hydration if returning from OAuth with #/sso-callback hash
  useEffect(() => {
    if (window.location.hash.includes('/sso-callback')) {
      setShowAuthModal(true)
      try {
        const saved = sessionStorage.getItem("transcriptr_pending_tool_url")
        if (saved) {
          const { redirectTo } = JSON.parse(saved)
          if (redirectTo === "configure") {
            setRedirectingToConfigure(true)
          }
        }
      } catch {}
    }
  }, [])

  // Clip Preview State
  const [previewClips, setPreviewClips] = useState<ClipPreview[] | null>(null)
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false)

  // Mobile layout state
  const [mobileTranscriptOpen, setMobileTranscriptOpen] = useState(false)

  // Paywall State
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)
  const [creditsInfo, setCreditsInfo] = useState<{ required: number; available: number } | null>(null)

  // Refs
  const workspaceRef = useRef<HTMLDivElement>(null)

  // Track tool page view
  useEffect(() => {
    ToolAnalytics.pageViewed(tool.slug, tool.name)
  }, [tool.slug, tool.name])

  // Listen for paywall events from backend (402 responses)
  useEffect(() => {
    const handlePaywallTrigger = () => {
      setIsPaywallOpen(true)
      setIsGenerating(false)
      setGenerationError("Free limit reached. Sign in or upgrade for unlimited generations.")
    }

    const handleInsufficientCredits = (event: any) => {
      console.log('🎯 ToolPageClient: Received insufficient-credits event', event.detail)
      const detail = event.detail || {}
      const creditsData = {
        required: detail.required || 0,
        available: detail.available || 0
      }
      console.log('💳 Setting credits info:', creditsData)
      setCreditsInfo(creditsData)
      setIsPaywallOpen(true)
      setIsGenerating(false)
      setGenerationError(`Insufficient credits. Required: ${creditsData.required}, Available: ${creditsData.available}`)
      console.log('✅ Modal should now be open')
    }

    apiEvents.addEventListener("paywall-triggered", handlePaywallTrigger)
    apiEvents.addEventListener("insufficient-credits", handleInsufficientCredits)

    return () => {
      apiEvents.removeEventListener("paywall-triggered", handlePaywallTrigger)
      apiEvents.removeEventListener("insufficient-credits", handleInsufficientCredits)
    }
  }, [])

  // Track if user was previously signed out (to detect sign-in transitions)
  const wasSignedIn = useRef(isSignedIn)
  useEffect(() => {
    // Detect sign-in transition: was signed out, now signed in
    if (isSignedIn && !wasSignedIn.current) {
      wasSignedIn.current = true

      // If redirecting to configure, don't close modal — loading overlay covers the UI
      if (!redirectingToConfigure) {
        setShowAuthModal(false)
      }

      // Check if "Try Free" triggered auth — redirect to /configure
      if (authRedirectTarget === "configure" || redirectingToConfigure) {
        const youtubeUrl = url || (transcript ? `https://youtube.com/watch?v=${transcript.youtube_video_id}` : "")
        setAuthRedirectTarget("tool") // Reset for future auth flows
        // Use window.location.href for OAuth redirects to ensure clean navigation
        const configureUrl = `/configure${youtubeUrl ? `?url=${encodeURIComponent(youtubeUrl)}` : ""}`
        if (redirectingToConfigure) {
          sessionStorage.removeItem("transcriptr_pending_tool_url")
          window.location.href = `${window.location.origin}${configureUrl}`
        } else {
          router.push(configureUrl)
        }
        return
      }

      // User just signed in via modal (email/password) — transcript is already loaded, generate
      if (transcript) {
        generateContent(transcript.youtube_video_id)
        return
      }

      // OAuth redirect recovery: force full page load to the correct URL with ?url= param.
      // This guarantees useSearchParams() works on the fresh page load.
      const saved = sessionStorage.getItem("transcriptr_pending_tool_url")
      if (saved) {
        try {
          const { youtubeUrl, redirectTo } = JSON.parse(saved)
          if (redirectTo === "configure" && youtubeUrl) {
            sessionStorage.removeItem("transcriptr_pending_tool_url")
            window.location.href = `${window.location.origin}/configure?url=${encodeURIComponent(youtubeUrl)}`
            return
          }
          if (youtubeUrl) {
            // Don't remove sessionStorage — the reload's second effect will handle it
            window.location.href = `${window.location.origin}/tools/${tool.slug}?url=${encodeURIComponent(youtubeUrl)}`
            return
          }
        } catch {}
      }
    }
    wasSignedIn.current = isSignedIn
  }, [isSignedIn])

  // Handle page load with ?url= param (from OAuth redirect recovery, forceRedirectUrl, or server-provided initialUrl)
  const hasAutoSubmitted = useRef(false)
  useEffect(() => {
    if (!isSignedIn || hasAutoSubmitted.current) return

    const urlParam = searchParams.get("url")
    if (urlParam) {
      hasAutoSubmitted.current = true
      // Clean up sessionStorage now that we have the URL from params
      sessionStorage.removeItem("transcriptr_pending_tool_url")
      setUrl(urlParam)
      setTimeout(() => handleSubmitWithUrl(urlParam), 0)
      return
    }

    // Fallback: check sessionStorage (in case URL param wasn't preserved)
    const saved = sessionStorage.getItem("transcriptr_pending_tool_url")
    if (saved) {
      sessionStorage.removeItem("transcriptr_pending_tool_url")
      try {
        const { youtubeUrl } = JSON.parse(saved)
        if (youtubeUrl) {
          hasAutoSubmitted.current = true
          setUrl(youtubeUrl)
          setTimeout(() => handleSubmitWithUrl(youtubeUrl), 0)
        }
      } catch {}
    }
  }, [isSignedIn])

  // Fetch clip previews as soon as transcript is available (non-blocking)
  useEffect(() => {
    if (!transcript || tool.id === 'viral_clips') return

    let cancelled = false
    setIsLoadingPreviews(true)

    api.tools.getClipPreviews(transcript.youtube_video_id)
      .then((res: { data?: { clips?: ClipPreview[] } }) => {
        if (!cancelled) setPreviewClips(res.data?.clips || [])
      })
      .catch(() => {
        // Non-critical — silently fail, static CTA still shows
        if (!cancelled) setPreviewClips(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPreviews(false)
      })

    return () => { cancelled = true }
  }, [transcript, tool.id])

  // Get tool icon
  const ToolIcon = getIconByName(tool.icon_name)

  // Extract video ID from YouTube URL
  const extractVideoId = (url: string): string | null => {
    const regex = /(?:v=|\/|embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    const match = url.match(regex)
    return match ? match[1] : null
  }

  // Format timestamp
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  // Copy transcript
  const copyTranscript = () => {
    if (!transcript) return

    const text = transcript.transcript.snippets
      .map((snippet) => {
        if (showTimestamps) {
          return `${formatTime(snippet.start)} ${snippet.text}`
        }
        return snippet.text
      })
      .join("\n")

    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Copy generated content
  const copyGenerated = () => {
    if (!generatedContent) return
    navigator.clipboard.writeText(generatedContent)
    ToolAnalytics.resultCopied(tool.id, transcript?.youtube_video_id)
    setCopiedGenerated(true)
    setTimeout(() => setCopiedGenerated(false), 2000)
  }

  // Handle form submit - fetch transcript and generate content
  const handleSubmitWithUrl = async (submitUrl: string) => {
    if (!submitUrl.trim()) return

    setLoading(true)
    setError(null)
    setGenerationError(null)
    setGeneratedContent("")
    setPreviewClips(null)

    try {
      // 1. Fetch transcript
      console.log('🎬 Fetching transcript for:', submitUrl)
      const transcriptResponse = await api.transcript.getTranscript(submitUrl)
      setTranscript(transcriptResponse.data)

      // 2. Show workspace
      setShowWorkspace(true)

      // 3. Scroll to workspace
      setTimeout(() => {
        workspaceRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 100)

      // 4. Auto-generate content only if signed in
      if (isSignedIn) {
        console.log(`🤖 Auto-generating ${tool.id} for video ${transcriptResponse.data.youtube_video_id}`)
        await generateContent(transcriptResponse.data.youtube_video_id)
      }

    } catch (err: any) {
      console.error('❌ Error:', err)
      setError(err.response?.data?.detail?.[0]?.msg || err.message || "Failed to fetch transcript")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => handleSubmitWithUrl(url)

  // Generate AI content
  const generateContent = async (videoId: string) => {
    if (!isSignedIn) {
      sessionStorage.setItem("transcriptr_pending_tool_url", JSON.stringify({ youtubeUrl: url }))
      setShowAuthModal(true)
      return
    }

    setIsGenerating(true)
    setGenerationError(null)
    setGeneratedContent("")
    const generationStart = Date.now()
    ToolAnalytics.generationStarted(tool.id, videoId, 'tool_page')

    try {
      const token = await getToken()

      console.log(`📡 Streaming ${tool.id} generation...`)
      const response = await api.tools.streamToolGeneration(
        videoId,
        tool.id, // Use the specific tool for this page
        "English",
        undefined,
        token || undefined
      )

      console.log('✅ Got streaming response')

      // Stream content progressively
      const reader = createReader(response.body)
      const chunks = readStream<string>(reader)

      for await (const chunk of chunks) {
        setGeneratedContent(prev => prev + chunk)
        await new Promise(resolve => setTimeout(resolve, 0))
      }

      console.log('✅ Generation complete')
      ToolAnalytics.generationCompleted(tool.id, videoId, Date.now() - generationStart)

    } catch (err: any) {
      console.error('❌ Generation error:', err)
      setGenerationError(err.message || "Failed to generate content")
    } finally {
      setIsGenerating(false)
    }
  }

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit()
    }
  }

  // Handle "Try Free" CTA from ClipPreviewPanel — routes to /configure
  const handleTryFree = () => {
    const youtubeUrl = url || (transcript ? `https://youtube.com/watch?v=${transcript.youtube_video_id}` : "")
    const configureUrl = `/configure${youtubeUrl ? `?url=${encodeURIComponent(youtubeUrl)}` : ""}`

    if (isSignedIn) {
      router.push(configureUrl)
      return
    }

    // Not signed in — save configure target and show auth modal
    sessionStorage.setItem("transcriptr_pending_tool_url", JSON.stringify({ youtubeUrl, redirectTo: "configure" }))
    setAuthRedirectTarget("configure")
    setAuthMode("sign-up")
    setShowAuthModal(true)
  }

  return (
    <div className="dark min-h-screen bg-[oklch(0.13_0.004_270)] text-white">
      {/* Paywall Modal */}
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        videoTitle={transcript?.title}
        creditsInfo={creditsInfo}
        surface="tool"
        toolSlug={tool.slug}
      />

      {/* OAuth → Configure redirect loading overlay */}
      {redirectingToConfigure && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-[oklch(0.13_0.004_270)]">
          <div className="w-8 h-8 border-2 border-[#CC3E21] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-zinc-400">Setting up your clip editor...</p>
        </div>
      )}

      {/* Auth Modal Overlay */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowAuthModal(false)}
          />
          <div className="relative z-10">
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute -top-3 -right-3 z-20 w-8 h-8 rounded-full bg-white dark:bg-zinc-800 shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            >
              <X className="h-4 w-4" />
            </button>
            {(() => {
              const youtubeUrl = url || (transcript ? `https://youtube.com/watch?v=${transcript.youtube_video_id}` : "")
              const redirectUrl = authRedirectTarget === "configure"
                ? `${window.location.origin}/configure${youtubeUrl ? `?url=${encodeURIComponent(youtubeUrl)}` : ""}`
                : `${window.location.origin}/tools/${tool.slug}${youtubeUrl ? `?url=${encodeURIComponent(youtubeUrl)}` : ""}`
              return authMode === "sign-up" ? (
                <SignUp
                  routing="hash"
                  forceRedirectUrl={redirectUrl}
                  signInForceRedirectUrl={redirectUrl}
                />
              ) : (
                <SignIn
                  routing="hash"
                  forceRedirectUrl={redirectUrl}
                  signUpForceRedirectUrl={redirectUrl}
                />
              )
            })()}
            <div className="text-center mt-3">
              {authMode === "sign-up" ? (
                <button
                  onClick={() => setAuthMode("sign-in")}
                  className="text-sm text-white/50 hover:text-white transition-colors"
                >
                  Already have an account? <span className="text-[#ff6b4a] font-medium">Sign in</span>
                </button>
              ) : (
                <button
                  onClick={() => setAuthMode("sign-up")}
                  className="text-sm text-white/50 hover:text-white transition-colors"
                >
                  Don&apos;t have an account? <span className="text-[#ff6b4a] font-medium">Sign up free</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header with Clerk Auth */}
      <Header />

      {/* Hero Section with URL Input */}
      <section className="border-b border-white/[0.06] pt-16">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Tool Icon */}
            <div className="w-20 h-20 bg-[#CC3E21]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ToolIcon className="h-10 w-10 text-[#ff6b4a]" />
            </div>

            {/* H1 */}
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {tool.h1}
            </h1>

            {/* Description */}
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {tool.long_description}
            </p>

            {/* URL Input Form */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-4">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Paste YouTube URL here..."
                className="flex-1 h-12 bg-white/[0.06] border border-white/[0.1] focus:border-[#CC3E21]/50 text-base"
                disabled={loading}
              />
              <Button
                onClick={handleSubmit}
                disabled={!url.trim() || loading}
                size="lg"
                className="h-12 bg-[#CC3E21] hover:bg-[#b5351b] text-white px-8 font-semibold whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Generate ${tool.name}`
                )}
              </Button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center justify-center gap-2 text-red-600 text-sm mb-4">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            {/* Secondary CTA */}
            {!showWorkspace && (
              <div className="flex gap-4 justify-center flex-wrap mt-8">
                <Link href="#how-it-works">
                  <Button size="lg" variant="outline">
                    Learn How It Works
                  </Button>
                </Link>
              </div>
            )}

            <p className="text-sm text-muted-foreground mt-4">
              No credit card required • Free to start • Instant results
            </p>
          </div>
        </div>
      </section>

      {/* Two-Column Workspace (appears after URL submitted) */}
      {showWorkspace && transcript && (
        <section ref={workspaceRef} className="py-8 border-b bg-muted/10">
          <div className="container mx-auto px-4">
            {/* Mobile guest layout — reordered for conversion:
                Video → Sign-up CTA → Collapsible Transcript → Clip Previews */}
            {!isSignedIn && transcript && (
              <div className="lg:hidden space-y-4 mb-8">
                {/* Video Player */}
                {url && extractVideoId(url) && (
                  <div className="aspect-video bg-black rounded-lg relative overflow-hidden">
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${extractVideoId(url)}`}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* Sign-up CTA */}
                <Card className="p-6 border-[#CC3E21]/20 bg-gradient-to-b from-[#CC3E21]/5 to-transparent">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-xl flex items-center justify-center mb-4">
                      <ToolIcon className="h-6 w-6 text-[#ff6b4a]" />
                    </div>
                    <h2 className="text-lg font-bold mb-1">
                      Sign Up to Generate {tool.name}
                    </h2>
                    <p className="text-xs text-muted-foreground mb-4 max-w-sm">
                      Your transcript is ready — sign up to generate results instantly.
                    </p>
                    <Button
                      size="default"
                      className="bg-[#CC3E21] hover:bg-[#b5351b] text-white px-6 gap-2"
                      onClick={() => { setAuthRedirectTarget("tool"); setAuthMode("sign-up"); setShowAuthModal(true) }}
                    >
                      <UserPlus className="h-4 w-4" />
                      Sign Up Free
                    </Button>
                    <p className="text-xs text-muted-foreground mt-3">
                      Already have an account?{' '}
                      <button
                        onClick={() => { setAuthRedirectTarget("tool"); setAuthMode("sign-in"); setShowAuthModal(true) }}
                        className="text-[#ff6b4a] hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </Card>

                {/* Transcript Preview + Expandable */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#ff6b4a]" />
                      <span className="text-sm font-medium">Transcript</span>
                    </div>
                    <button onClick={copyTranscript} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
                      <Copy className={`h-4 w-4 ${copied ? "text-emerald-400" : "text-muted-foreground"}`} />
                    </button>
                  </div>

                  {/* Preview: first 5 snippets always visible */}
                  <div className="space-y-1 text-sm border rounded-lg p-3">
                    {transcript.transcript.snippets.slice(0, 5).map((snippet, index) => (
                      <div
                        key={index}
                        className="hover:bg-muted p-1.5 rounded cursor-pointer transition-colors"
                        onClick={() => {
                          const iframe = document.querySelector("iframe")
                          if (iframe) {
                            iframe.src = `https://www.youtube.com/embed/${extractVideoId(url)}?start=${Math.floor(snippet.start)}&autoplay=1`
                          }
                        }}
                      >
                        <span className="text-muted-foreground mr-2 text-xs">{formatTime(snippet.start)}</span>
                        <span className="text-sm">{snippet.text}</span>
                      </div>
                    ))}

                    {/* Expanded: remaining snippets */}
                    {mobileTranscriptOpen && transcript.transcript.snippets.length > 5 && (
                      <>
                        {/* Controls — shown when expanded */}
                        <div className="grid grid-cols-2 gap-2 my-3">
                          <Button
                            size="sm"
                            onClick={copyTranscript}
                            className="bg-[#CC3E21] hover:bg-[#b5351b] text-white text-xs"
                          >
                            <Copy className="h-3 w-3 mr-1.5" />
                            {copied ? "Copied!" : "Copy All"}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setShowTimestamps(!showTimestamps)}
                            className="bg-[#CC3E21] hover:bg-[#b5351b] text-white text-xs"
                          >
                            <Clock className="h-3 w-3 mr-1.5" />
                            Timestamps {showTimestamps ? "ON" : "OFF"}
                          </Button>
                        </div>

                        <div className="max-h-[400px] overflow-y-auto space-y-1">
                          {transcript.transcript.snippets.slice(5).map((snippet, index) => (
                            <div
                              key={index + 5}
                              className="hover:bg-muted p-1.5 rounded cursor-pointer transition-colors"
                              onClick={() => {
                                const iframe = document.querySelector("iframe")
                                if (iframe) {
                                  iframe.src = `https://www.youtube.com/embed/${extractVideoId(url)}?start=${Math.floor(snippet.start)}&autoplay=1`
                                }
                              }}
                            >
                              {showTimestamps && (
                                <span className="text-muted-foreground mr-2 text-xs">{formatTime(snippet.start)}</span>
                              )}
                              <span className="text-sm">{snippet.text}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Expand/Collapse toggle */}
                  {transcript.transcript.snippets.length > 5 && (
                    <button
                      onClick={() => setMobileTranscriptOpen(!mobileTranscriptOpen)}
                      className="w-full flex items-center justify-center gap-1.5 mt-3 py-2 text-xs text-muted-foreground hover:text-white transition-colors"
                    >
                      {mobileTranscriptOpen ? (
                        <>Show less <ChevronUp className="h-3 w-3" /></>
                      ) : (
                        <>Show {transcript.transcript.snippets.length - 5} more lines <ChevronDown className="h-3 w-3" /></>
                      )}
                    </button>
                  )}
                </Card>

                {/* Clip Previews */}
                {tool.id !== 'viral_clips' && (
                  <ClipPreviewPanel
                    clips={previewClips || []}
                    videoId={transcript.youtube_video_id}
                    isLoading={isLoadingPreviews}
                    onTryFree={handleTryFree}
                  />
                )}
              </div>
            )}

            {/* Desktop Layout (hidden on mobile for guests): Side-by-side columns */}
            <div className={`grid lg:grid-cols-2 gap-8 ${!isSignedIn && transcript ? 'hidden lg:grid' : ''}`}>
              {/* Left Column: Video + Transcript */}
              <div className="lg:sticky lg:top-24 lg:self-start">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Transcript</h2>
                    <button onClick={copyTranscript} className="p-2 hover:bg-muted rounded-lg transition-colors">
                      <Copy className={`h-5 w-5 ${copied ? "text-emerald-400" : "text-[#ff6b4a]"}`} />
                    </button>
                  </div>

                  {/* Video Player */}
                  {url && extractVideoId(url) && (
                    <div className="aspect-video bg-black rounded-lg mb-4 relative overflow-hidden">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${extractVideoId(url)}`}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}

                  {/* Mobile Only: Compact Generation Status Bar */}
                  <div className="lg:hidden mb-4">
                    {isGenerating ? (
                      <Card className="p-3 border-[#CC3E21]/20 bg-[#CC3E21]/5">
                        <div className="flex items-center gap-3">
                          <Loader2 className="h-5 w-5 animate-spin text-[#ff6b4a] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#ff6b4a] truncate">
                              Generating {tool.name}...
                            </p>
                          </div>
                        </div>
                      </Card>
                    ) : generatedContent ? (
                      <Card className="p-3 border-green-200 bg-green-50">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Check className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-green-800">{tool.name} ready</p>
                          </div>
                          <span className="text-xs text-emerald-400">↓ Scroll to view</span>
                        </div>
                      </Card>
                    ) : generationError ? (
                      <Card className="p-3 border-red-200 bg-red-50">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                          <p className="text-sm font-medium text-red-800 truncate">{generationError}</p>
                        </div>
                      </Card>
                    ) : null}
                  </div>

                  {/* Transcript Controls */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Button
                      onClick={copyTranscript}
                      className="bg-[#CC3E21] hover:bg-[#b5351b] text-white"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                    <Button
                      onClick={() => setShowTimestamps(!showTimestamps)}
                      className="bg-[#CC3E21] hover:bg-[#b5351b] text-white"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Timestamp {showTimestamps ? "ON" : "OFF"}
                    </Button>
                  </div>

                  {/* Transcript Text */}
                  <div className="space-y-1 max-h-[400px] lg:max-h-[600px] overflow-y-auto text-sm border rounded-lg p-3">
                    {transcript.transcript.snippets.map((snippet, index) => (
                      <div
                        key={index}
                        className="hover:bg-muted p-2 rounded cursor-pointer transition-colors"
                        onClick={() => {
                          const iframe = document.querySelector("iframe")
                          if (iframe) {
                            iframe.src = `https://www.youtube.com/embed/${extractVideoId(url)}?start=${Math.floor(snippet.start)}&autoplay=1`
                          }
                        }}
                      >
                        {showTimestamps && (
                          <span className="text-muted-foreground mr-2 text-xs">{formatTime(snippet.start)}</span>
                        )}
                        <span className="text-sm">{snippet.text}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Right Column: Generated Content or Sign-up CTA */}
              <div>
                {/* Sign-up CTA for guests who have a transcript but no generated content */}
                {!isSignedIn && transcript && !generatedContent && !isGenerating && (
                  <Card className="p-8 border-[#CC3E21]/20 bg-gradient-to-b from-[#CC3E21]/5 to-transparent">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-[#CC3E21]/10 rounded-2xl flex items-center justify-center mb-6">
                        <ToolIcon className="h-8 w-8 text-[#ff6b4a]" />
                      </div>
                      <h2 className="text-xl font-bold mb-2">
                        Sign Up to Generate {tool.name}
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                        Create a free account to use our AI-powered tools. Your transcript is ready — sign up to generate results instantly.
                      </p>
                      <Button
                        size="lg"
                        className="bg-[#CC3E21] hover:bg-[#b5351b] text-white px-8 gap-2"
                        onClick={() => { setAuthRedirectTarget("tool"); setAuthMode("sign-up"); setShowAuthModal(true) }}
                      >
                        <UserPlus className="h-4 w-4" />
                        Sign Up Free
                      </Button>
                      <p className="text-xs text-muted-foreground mt-4">
                        Already have an account?{' '}
                        <button
                          onClick={() => { setAuthRedirectTarget("tool"); setAuthMode("sign-in"); setShowAuthModal(true) }}
                          className="text-[#ff6b4a] hover:underline"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </Card>
                )}

                {/* Clip Preview Panel — shows for guests with transcript */}
                {!isSignedIn && transcript && tool.id !== 'viral_clips' && (
                  <ClipPreviewPanel
                    clips={previewClips || []}
                    videoId={transcript.youtube_video_id}
                    isLoading={isLoadingPreviews}
                    onTryFree={handleTryFree}
                  />
                )}

                {/* Authenticated content card */}
                {(isSignedIn || !transcript || generatedContent || isGenerating) && (
                <Card className="p-6">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold">{tool.name}</h2>
                    <p className="text-sm text-muted-foreground">{tool.short_description}</p>
                  </div>

                  {/* Loading State */}
                  {isGenerating && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-[#ff6b4a] mb-4" />
                      <p className="text-sm text-muted-foreground">Generating {tool.name.toLowerCase()}...</p>
                    </div>
                  )}

                  {/* Error State */}
                  {generationError && !isGenerating && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <AlertCircle className="h-8 w-8 text-red-600 mb-4" />
                      <p className="text-sm text-red-600">{generationError}</p>
                    </div>
                  )}

                  {/* Generated Content */}
                  {generatedContent && (
                    <div className="space-y-4">
                      <div className="prose prose-sm max-w-none">
                        <MarkdownRenderer content={generatedContent} />
                      </div>

                      {/* Copy Button */}
                      {!isGenerating && (
                        <div className="flex gap-2 pt-4 border-t">
                          <Button onClick={copyGenerated} className="bg-[#CC3E21] hover:bg-[#b5351b]">
                            <Copy className="h-4 w-4 mr-2" />
                            {copiedGenerated ? "Copied!" : "Copy Result"}
                          </Button>
                        </div>
                      )}

                      {/* Clip Preview Panel — shows after generation completes */}
                      {!isGenerating && transcript && tool.id !== 'viral_clips' && (
                        <ClipPreviewPanel
                          clips={previewClips || []}
                          videoId={transcript.youtube_video_id}
                          isLoading={isLoadingPreviews}
                          onTryFree={handleTryFree}
                        />
                      )}
                    </div>
                  )}

                  {/* Empty State — only show when signed in or no transcript yet */}
                  {!isGenerating && !generatedContent && !generationError && (isSignedIn || !transcript) && (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                        <ToolIcon className="h-8 w-8 text-[#ff6b4a]" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Paste a YouTube URL above to generate {tool.name.toLowerCase()}
                      </p>
                    </div>
                  )}
                </Card>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">
              How to Use {tool.name}
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">1</span>
                </div>
                <h3 className="font-semibold mb-2">Paste YouTube URL</h3>
                <p className="text-sm text-muted-foreground">
                  Copy any YouTube video link and paste it into our tool.
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">2</span>
                </div>
                <h3 className="font-semibold mb-2">Generate Content</h3>
                <p className="text-sm text-muted-foreground">
                  Click generate and our AI will process the video transcript instantly.
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#ff6b4a]">3</span>
                </div>
                <h3 className="font-semibold mb-2">Copy & Use</h3>
                <p className="text-sm text-muted-foreground">
                  Copy your result and use it for studying, content creation, or research.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Perfect For
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {tool.use_cases.map((useCase: string, index: number) => (
                <div key={index} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-muted-foreground">{useCase}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Example Use Cases
            </h2>

            <div className="space-y-4">
              {tool.examples.map((example: string, index: number) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ToolIcon className="h-4 w-4 text-[#ff6b4a]" />
                    </div>
                    <p>{example}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools */}
      {relatedTools.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">
                Related Tools
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                {relatedTools.map((relatedTool: any) => {
                  const RelatedIcon = getIconByName(relatedTool.icon_name)
                  return (
                    <Link key={relatedTool.id} href={`/tools/${relatedTool.slug}`}>
                      <Card className="p-6 hover:border-[#CC3E21]/50 transition-colors cursor-pointer h-full">
                        <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                          <RelatedIcon className="h-6 w-6 text-[#ff6b4a]" />
                        </div>
                        <h3 className="font-semibold mb-2">{relatedTool.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {relatedTool.short_description}
                        </p>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Viral Clips Funnel CTA — hidden when clip preview panel is showing */}
      {tool.id !== 'viral_clips' && !(previewClips && previewClips.length > 0) && (
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link href="/tools/youtube-viral-clips">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#CC3E21] to-[#ff6b4a] p-8 md:p-10 text-white group hover:shadow-2xl hover:shadow-[#CC3E21]/20 transition-all">
                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Zap className="h-7 w-7" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-xl md:text-2xl font-bold mb-1">
                        Turn This Video Into Viral Clips
                      </h3>
                      <p className="text-white/80 text-sm md:text-base">
                        AI detects the most shareable moments, scores them 0-100, and styles them with captions and B-roll. Try it free.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#CC3E21] font-bold rounded-xl group-hover:scale-105 transition-transform">
                        Find Viral Clips <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                  {/* Decorative background circles */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* More Tools from Category */}
      {categoryTools.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">
                More {tool.category.charAt(0).toUpperCase() + tool.category.slice(1)} Tools
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                {categoryTools.map((categoryTool: any) => {
                  const CategoryIcon = getIconByName(categoryTool.icon_name)
                  return (
                    <Link key={categoryTool.id} href={`/tools/${categoryTool.slug}`}>
                      <Card className="p-6 hover:border-[#CC3E21]/50 transition-colors cursor-pointer h-full">
                        <div className="w-12 h-12 bg-[#CC3E21]/10 rounded-lg flex items-center justify-center mb-4">
                          <CategoryIcon className="h-6 w-6 text-[#ff6b4a]" />
                        </div>
                        <h3 className="font-semibold mb-2">{categoryTool.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {categoryTool.short_description}
                        </p>
                      </Card>
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Start using {tool.name} for free. No credit card required.
            </p>
            <Button
              size="lg"
              className="gap-2"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
                setTimeout(() => document.querySelector<HTMLInputElement>('input[placeholder*="YouTube"]')?.focus(), 600)
              }}
            >
              Try {tool.name} Free <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Frequently Asked Questions
            </h2>

            {/* If the tool ships per-tool FAQs from the backend, render them.
                Otherwise fall back to the generic Q&A so the section is never
                empty. Rendering tool.faqs in the DOM keeps the visible copy
                consistent with the FAQPage JSON-LD emitted in page.tsx. */}
            <div className="space-y-6">
              {tool.faqs && Array.isArray(tool.faqs) && tool.faqs.length > 0 ? (
                tool.faqs.map((faq: { q: string; a: string }, idx: number) => (
                  <Card key={idx} className="p-6">
                    <h3 className="font-semibold mb-2">{faq.q}</h3>
                    <p className="text-muted-foreground whitespace-pre-line">{faq.a}</p>
                  </Card>
                ))
              ) : (
                <>
                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">
                      Is {tool.name} really free?
                    </h3>
                    <p className="text-muted-foreground">
                      Yes! You can try {tool.name} for free with limited usage.
                      For unlimited access to all 30+ tools, upgrade to our premium plan.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">
                      What video lengths can I process?
                    </h3>
                    <p className="text-muted-foreground">
                      You can process YouTube videos of any length, from short clips
                      to multi-hour lectures and podcasts.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">
                      How accurate is the AI generation?
                    </h3>
                    <p className="text-muted-foreground">
                      Our AI uses advanced language models trained on millions of videos.
                      Results are highly accurate, but we recommend reviewing the output
                      for critical applications.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-2">
                      Can I use this for commercial purposes?
                    </h3>
                    <p className="text-muted-foreground">
                      Yes! Premium subscribers can use generated content for commercial
                      purposes including blogs, social media, and business documentation.
                    </p>
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Mobile Sign-Up CTA — visible only on mobile when guest has transcript */}
      <MobileSignUpCTA
        ctaText={`Sign Up Free to Generate ${tool.name}`}
        onCtaClick={() => { setAuthMode("sign-up"); setShowAuthModal(true) }}
        isVisible={!isSignedIn && !!transcript && !generatedContent && !isGenerating}
      />
    </div>
  )
}
